import { productFruits } from 'product-fruits'
import type { UserData } from '~/stores/user'
import { useRuntimeConfig, useNuxtApp } from '#app'
import { useRouter } from '#vue-router'
import { useLogger } from '#layers/base/composables/useLogger'
import { useCountryConfig } from '~/composables/useCountryConfig'

export default defineNuxtPlugin(() => {
  const workspaceCode = useCountryConfig().productFruitsCode
  const { productFruitsEnabled } = useRuntimeConfig().public
  const router = useRouter()
  const locale = useNuxtApp().$i18n.locale
  const log = useLogger()
  const { setFirstOrderCompleted, destroy } = useProductFruits()

  if (!productFruitsEnabled || !workspaceCode)
    return

  let isInitialized = false
  const orderHistory = useOrderHistoryStore()

  const toSignUpAt = (dateCreated: string | undefined) => {
    if (!dateCreated)
      return undefined
    const parsed = new Date(dateCreated.includes('T') ? dateCreated : dateCreated.replace(' ', 'T'))
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
  }

  const initProductFruits = (user: UserData) => {
    if (isInitialized || !workspaceCode)
      return
    log.trace('Init product-fruits', { user: user.email })

    isInitialized = true
    const signUpAt = toSignUpAt(user.dateCreated)

    productFruits.init(
      workspaceCode,
      locale.value,
      {
        username: user.email,
        email: user.email,
        firstname: user.firstname,
        lastname: user.lastname,
        ...(signUpAt ? { signUpAt } : {}),
        props: {
          firstOrderCompleted: orderHistory.hasAnyOrders,
        },
      },
      { disableLocationChangeDetection: true },
    )
  }

  router.afterEach(() => {
    if (!isInitialized)
      return
    productFruits.safeExec(($productFruits) => {
      $productFruits.push(['pageChanged'])
    })
  })

  watch(
    () => orderHistory.hasAnyOrders,
    (hasAnyOrders) => {
      if (isInitialized && hasAnyOrders)
        setFirstOrderCompleted(true)
    },
  )

  watch(
    () => useUserStore().data,
    (user) => {
      if (user) {
        initProductFruits(user)
        return
      }
      if (!isInitialized)
        return
      destroy()
      isInitialized = false
    },
    { immediate: true },
  )
})
